import { useState, useMemo } from "react";
import { STATE_REACH, TOTAL_DISTRICTS } from "../../data/ybf/states";
import { PILLARS } from "../../data/ybf/pillars";
import { fmtInt, fmtCr } from "../../data/ybf/utils";
import { MapPin, Building2, Users, IndianRupee, X } from "lucide-react";
import YBFIndiaMap from "../../components/ybf/YBFIndiaMap";
import PillarStateHeatmap from "../../components/ybf/PillarStateHeatmap";

export default function Geography() {
  const [selected, setSelected] = useState(null);
  const totals = useMemo(() => ({
    states: STATE_REACH.length,
    districts: TOTAL_DISTRICTS,
    beneficiaries: STATE_REACH.reduce((s, x) => s + x.beneficiaries, 0),
    funded: STATE_REACH.reduce((s, x) => s + x.fundedCr, 0),
  }), []);

  return (
    <div data-testid="ybf-geography" className="space-y-5 pb-8">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h1 className="text-2xl font-bold text-slate-900">Geographic Reach</h1>
        <p className="text-sm text-slate-600 mt-1">Lenovo CSR is active across {totals.states} states and {totals.districts}+ districts of India.</p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={MapPin}      label="States"        value={totals.states}        accent="#1A1A1A" />
        <Tile icon={Building2}   label="Districts"     value={totals.districts}     accent="#6B7280" />
        <Tile icon={Users}       label="Beneficiaries" value={fmtInt(totals.beneficiaries)} accent="#E2231A" />
        <Tile icon={IndianRupee} label="Sanctioned"    value={fmtCr(totals.funded)} accent="#B45309" />
      </div>

      {/* India Map */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Reach Across India</h3>
            <p className="text-[11px] text-slate-500">Darker = more beneficiaries reached. Hover a state for details, click to drill in.</p>
          </div>
        </div>
        <YBFIndiaMap onStateClick={(d) => setSelected(d)} />
      </div>

      {/* Pillar × State Heatmap */}
      <PillarStateHeatmap />

      {/* All states table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-900">All States — At a Glance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="states-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left font-medium text-slate-600 uppercase py-2 px-3">State</th>
                <th className="text-right font-medium text-slate-600 uppercase py-2 px-3">Districts</th>
                <th className="text-right font-medium text-slate-600 uppercase py-2 px-3">Beneficiaries</th>
                <th className="text-right font-medium text-slate-600 uppercase py-2 px-3">Sanctioned</th>
                <th className="text-left font-medium text-slate-600 uppercase py-2 px-3">Active Pillars</th>
              </tr>
            </thead>
            <tbody>
              {STATE_REACH.map((s) => (
                <tr
                  key={s.state}
                  className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer"
                  onClick={() => setSelected(s)}
                  data-testid={`state-row-${s.state}`}
                >
                  <td className="py-2 px-3 font-semibold text-slate-800">{s.state}</td>
                  <td className="py-2 px-3 text-right text-slate-700">{s.districts}</td>
                  <td className="py-2 px-3 text-right font-medium text-slate-800">{fmtInt(s.beneficiaries)}</td>
                  <td className="py-2 px-3 text-right font-medium text-slate-800">{fmtCr(s.fundedCr)}</td>
                  <td className="py-2 px-3">
                    <div className="flex flex-wrap gap-1">
                      {s.pillars.map((pid) => {
                        const p = PILLARS.find((x) => x.id === pid);
                        return (
                          <span key={pid} className={`text-[9px] px-1.5 py-0.5 rounded-full ${p?.chipBg} ${p?.chipText} font-medium`}>
                            {p?.short}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="state-detail-modal"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">State</div>
                <h3 className="text-xl font-bold text-slate-900">{selected.state}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700" data-testid="close-state-modal">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Mini label="Districts"     value={selected.districts} />
              <Mini label="Beneficiaries" value={fmtInt(selected.beneficiaries)} />
              <Mini label="Sanctioned"    value={fmtCr(selected.fundedCr)} />
              <Mini label="Pillars"       value={selected.pillars.length} />
            </div>
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Active Pillars</div>
              <div className="flex flex-wrap gap-1.5">
                {selected.pillars.map((pid) => {
                  const p = PILLARS.find((x) => x.id === pid);
                  return (
                    <span key={pid} className={`text-[11px] px-2 py-1 rounded-full ${p?.chipBg} ${p?.chipText} font-medium`}>
                      {p?.name}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}10`, color: accent }}>
        <Icon size={18} />
      </div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mt-3">{label}</div>
      <div className="text-2xl font-bold text-slate-900 leading-none mt-0.5">{value}</div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-base font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
